import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Users, 
  Activity, 
  TrendingUp,
  Search,
  RefreshCw,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Database,
  Zap,
  Server,
  FileText,
  Save
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';

const AdminPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobRuns, setJobRuns] = useState([]);
  const [rateLimiterStats, setRateLimiterStats] = useState(null);
  const [stockProvider, setStockProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [triggeringJob, setTriggeringJob] = useState(false);
  const [switchingProvider, setSwitchingProvider] = useState(false);
  
  // Legal pages editor
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [savingTerms, setSavingTerms] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, jobsRes, rateLimiterRes, providerRes, termsRes, privacyRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/jobs'),
        api.get('/admin/rate-limiter'),
        api.get('/admin/stock-provider'),
        api.get('/legal/terms'),
        api.get('/legal/privacy')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setJobRuns(jobsRes.data);
      setRateLimiterStats(rateLimiterRes.data);
      setStockProvider(providerRes.data);
      setTermsContent(termsRes.data.content || '');
      setPrivacyContent(privacyRes.data.content || '');
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [api, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const triggerIngestion = async () => {
    setTriggeringJob(true);
    try {
      await api.post('/admin/jobs/trigger-ingestion');
      toast.success('Price ingestion job triggered');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger job');
    } finally {
      setTriggeringJob(false);
    }
  };

  const switchProvider = async (provider) => {
    setSwitchingProvider(true);
    try {
      await api.post(`/admin/stock-provider?provider=${provider}`);
      toast.success(`Switched to ${provider.charAt(0).toUpperCase() + provider.slice(1)} provider`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch provider');
    } finally {
      setSwitchingProvider(false);
    }
  };

  const saveTerms = async () => {
    setSavingTerms(true);
    try {
      await api.put(`/admin/legal/terms?content=${encodeURIComponent(termsContent)}`);
      toast.success('Terms & Conditions saved');
    } catch (error) {
      toast.error('Failed to save Terms & Conditions');
    } finally {
      setSavingTerms(false);
    }
  };

  const savePrivacy = async () => {
    setSavingPrivacy(true);
    try {
      await api.put(`/admin/legal/privacy?content=${encodeURIComponent(privacyContent)}`);
      toast.success('Privacy Policy saved');
    } catch (error) {
      toast.error('Failed to save Privacy Policy');
    } finally {
      setSavingPrivacy(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.name && u.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-pulse-subtle text-primary font-serif text-xl">Loading admin panel...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="admin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-[#1F2937]">Admin Dashboard</h1>
            <p className="text-gray-500 mt-1">System administration and monitoring</p>
          </div>
          <Badge variant="outline" className="border-[#1A4D2E] text-[#1A4D2E]">
            <Shield className="w-3 h-3 mr-1" />
            Admin Access
          </Badge>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total Users</p>
                  <p className="text-2xl font-bold text-[#1F2937] mt-1" data-testid="total-users">
                    {stats?.total_users || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Active Tickers</p>
                  <p className="text-2xl font-bold text-[#1F2937] mt-1">
                    {stats?.active_tickers || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#1A4D2E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total Trades</p>
                  <p className="text-2xl font-bold text-[#1F2937] mt-1">
                    {stats?.total_trades || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
                  <Activity className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Paid Users</p>
                  <p className="text-2xl font-bold text-[#1F2937] mt-1">
                    {stats?.paid_users || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="users" className="data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="jobs" className="data-[state=active]:bg-white">
              <Activity className="w-4 h-4 mr-2" />
              Jobs
            </TabsTrigger>
            <TabsTrigger value="providers" className="data-[state=active]:bg-white">
              <Server className="w-4 h-4 mr-2" />
              Data Providers
            </TabsTrigger>
            <TabsTrigger value="legal" className="data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />
              Legal Pages
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-white border-stone-100 shadow-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="font-serif text-xl text-[#1F2937]">User Management</CardTitle>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead>2FA</TableHead>
                        <TableHead>Cash Balance</TableHead>
                        <TableHead>Trades</TableHead>
                        <TableHead>Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                            No users found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredUsers.map((u) => (
                          <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-[#1F2937]">{u.name || 'No name'}</p>
                                <p className="text-sm text-gray-500">{u.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={u.tier === 'paid' ? 'default' : 'secondary'}
                                className={u.tier === 'paid' ? 'bg-[#1A4D2E]' : ''}
                              >
                                {u.tier}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {u.two_fa_enabled ? (
                                <span className="flex items-center text-green-600">
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Enabled
                                </span>
                              ) : (
                                <span className="flex items-center text-gray-400">
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Disabled
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="tabular-nums">
                              ${u.cash_balance?.toFixed(2) || '10,000.00'}
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {u.trades_this_month || 0}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {formatDate(u.created_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <div className="space-y-6">
              {/* Job Controls */}
              <Card className="bg-white border-stone-100 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-[#1F2937]">Price Ingestion Job</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <RefreshCw className="w-6 h-6 text-[#1A4D2E]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1F2937]">Daily Price Update</p>
                        <p className="text-sm text-gray-500">
                          Fetches latest prices for all watched tickers
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={triggerIngestion}
                      disabled={triggeringJob}
                      className="bg-[#1A4D2E] hover:bg-[#143d24]"
                      data-testid="trigger-ingestion-btn"
                    >
                      {triggeringJob ? (
                        <span className="flex items-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Running...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <RefreshCw className="w-4 h-4" />
                          Run Now
                        </span>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Job History */}
              <Card className="bg-white border-stone-100 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-[#1F2937]">Job History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Job</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Started</TableHead>
                          <TableHead>Finished</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {jobRuns.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                              No job runs yet
                            </TableCell>
                          </TableRow>
                        ) : (
                          jobRuns.map((job) => (
                            <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                              <TableCell>
                                <p className="font-medium text-[#1F2937]">{job.job_name}</p>
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  variant={
                                    job.status === 'success' ? 'default' : 
                                    job.status === 'failed' ? 'destructive' : 'secondary'
                                  }
                                  className={job.status === 'success' ? 'bg-green-600' : ''}
                                >
                                  {job.status === 'success' && <CheckCircle className="w-3 h-3 mr-1" />}
                                  {job.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                                  {job.status === 'partial' && <AlertTriangle className="w-3 h-3 mr-1" />}
                                  {job.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {formatDate(job.started_at)}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {formatDate(job.finished_at)}
                              </TableCell>
                              <TableCell className="text-sm tabular-nums">
                                {job.duration_ms ? `${job.duration_ms}ms` : 'N/A'}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">
                                {job.summary || 'No details'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Data Providers Tab */}
          <TabsContent value="providers">
            <div className="space-y-6">
              {/* Provider Switcher */}
              <Card className="bg-white border-stone-100 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-[#1F2937]">Stock Data Provider</CardTitle>
                  <CardDescription>Choose which API to use for stock data</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Finnhub */}
                    <div 
                      className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                        stockProvider?.active_provider === 'finnhub' 
                          ? 'border-[#1A4D2E] bg-green-50' 
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                      onClick={() => stockProvider?.active_provider !== 'finnhub' && switchProvider('finnhub')}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Zap className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1F2937]">Finnhub</h3>
                            <p className="text-xs text-gray-500">Real-time market data</p>
                          </div>
                        </div>
                        {stockProvider?.active_provider === 'finnhub' && (
                          <Badge className="bg-[#1A4D2E]">Active</Badge>
                        )}
                      </div>
                      <div className="space-y-1 text-sm">
                        <p className="flex items-center gap-2">
                          <CheckCircle className="w-3 h-3 text-green-600" />
                          <span>Quotes, Candles, Profiles, News</span>
                        </p>
                        <p className="flex items-center gap-2">
                          {stockProvider?.provider_status?.finnhub?.api_key_set ? (
                            <CheckCircle className="w-3 h-3 text-green-600" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-500" />
                          )}
                          <span>API Key: {stockProvider?.provider_status?.finnhub?.api_key_set ? 'Configured' : 'Not Set'}</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-2">Free: 60 calls/min</p>
                      </div>
                    </div>

                    {/* MarketStack */}
                    <div 
                      className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                        stockProvider?.active_provider === 'marketstack' 
                          ? 'border-[#1A4D2E] bg-green-50' 
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                      onClick={() => stockProvider?.active_provider !== 'marketstack' && switchProvider('marketstack')}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Database className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1F2937]">MarketStack</h3>
                            <p className="text-xs text-gray-500">Alternative data source</p>
                          </div>
                        </div>
                        {stockProvider?.active_provider === 'marketstack' && (
                          <Badge className="bg-[#1A4D2E]">Active</Badge>
                        )}
                      </div>
                      <div className="space-y-1 text-sm">
                        <p className="flex items-center gap-2">
                          <CheckCircle className="w-3 h-3 text-green-600" />
                          <span>Quotes, Search</span>
                        </p>
                        <p className="flex items-center gap-2">
                          {stockProvider?.provider_status?.marketstack?.api_key_set ? (
                            <CheckCircle className="w-3 h-3 text-green-600" />
                          ) : (
                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                          )}
                          <span>API Key: {stockProvider?.provider_status?.marketstack?.api_key_set ? 'Configured' : 'Mock Mode'}</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-2">Free: 100 calls/month • Paid: More</p>
                      </div>
                    </div>
                  </div>

                  {switchingProvider && (
                    <div className="flex items-center justify-center p-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-[#1A4D2E] mr-2" />
                      <span className="text-sm text-gray-500">Switching provider...</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Rate Limiter Stats */}
              <Card className="bg-white border-stone-100 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-[#1F2937]">Rate Limiter & Cache Stats</CardTitle>
                  <CardDescription>Monitor API usage and cache performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="p-4 bg-stone-50 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-gray-500">API Calls (Last Min)</p>
                      <p className="text-2xl font-bold text-[#1F2937] mt-1">
                        {rateLimiterStats?.finnhub_rate_limiter?.requests_last_minute || 0}
                        <span className="text-sm font-normal text-gray-400">/{rateLimiterStats?.limits?.max_requests_per_minute || 55}</span>
                      </p>
                    </div>
                    <div className="p-4 bg-stone-50 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-gray-500">Memory Cache</p>
                      <p className="text-2xl font-bold text-[#1F2937] mt-1">
                        {rateLimiterStats?.finnhub_rate_limiter?.cache_entries || 0}
                        <span className="text-sm font-normal text-gray-400"> entries</span>
                      </p>
                    </div>
                    <div className="p-4 bg-stone-50 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-gray-500">DB Cache</p>
                      <p className="text-2xl font-bold text-[#1F2937] mt-1">
                        {rateLimiterStats?.mongodb_cache_entries || 0}
                        <span className="text-sm font-normal text-gray-400"> entries</span>
                      </p>
                    </div>
                    <div className="p-4 bg-stone-50 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-gray-500">Pending Requests</p>
                      <p className="text-2xl font-bold text-[#1F2937] mt-1">
                        {rateLimiterStats?.finnhub_rate_limiter?.pending_requests || 0}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">Cache TTLs</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                      <div>
                        <span className="text-blue-600">Quotes:</span> {rateLimiterStats?.cache_ttls?.quote}
                      </div>
                      <div>
                        <span className="text-blue-600">Profiles:</span> {rateLimiterStats?.cache_ttls?.profile}
                      </div>
                      <div>
                        <span className="text-blue-600">Search:</span> {rateLimiterStats?.cache_ttls?.search}
                      </div>
                      <div>
                        <span className="text-blue-600">Candles:</span> {rateLimiterStats?.cache_ttls?.candles}
                      </div>
                      <div>
                        <span className="text-blue-600">News:</span> {rateLimiterStats?.cache_ttls?.news}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Legal Pages Tab */}
          <TabsContent value="legal">
            <div className="grid gap-6">
              {/* Terms Editor */}
              <Card className="bg-white border-stone-100 shadow-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-serif text-xl text-[#1F2937]">Terms & Conditions</CardTitle>
                      <CardDescription>Edit the Terms and Conditions page content (Markdown supported)</CardDescription>
                    </div>
                    <Button 
                      onClick={saveTerms}
                      disabled={savingTerms}
                      className="bg-[#1A4D2E] hover:bg-[#143d24]"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingTerms ? 'Saving...' : 'Save Terms'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={termsContent}
                    onChange={(e) => setTermsContent(e.target.value)}
                    className="min-h-[300px] font-mono text-sm"
                    placeholder="# Terms and Conditions..."
                  />
                </CardContent>
              </Card>

              {/* Privacy Editor */}
              <Card className="bg-white border-stone-100 shadow-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-serif text-xl text-[#1F2937]">Privacy Policy</CardTitle>
                      <CardDescription>Edit the Privacy Policy page content (Markdown supported)</CardDescription>
                    </div>
                    <Button 
                      onClick={savePrivacy}
                      disabled={savingPrivacy}
                      className="bg-[#1A4D2E] hover:bg-[#143d24]"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingPrivacy ? 'Saving...' : 'Save Privacy Policy'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={privacyContent}
                    onChange={(e) => setPrivacyContent(e.target.value)}
                    className="min-h-[300px] font-mono text-sm"
                    placeholder="# Privacy Policy..."
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default AdminPage;
